import React, {useCallback, useEffect} from 'react';
import {FieldsErrorResponse} from '@chekin/api';
import {getErrorMessage, isObject, STATUSES} from '@chekin/core';
import {ANALYTIC_EVENTS, Analytics} from '../../../analytics';

type UseIVProcessStateProps = {
  displayError: (error: Error | FieldsErrorResponse) => void;
  onCancelRetry: () => void;
  isKioskoScanning?: boolean;
  onError?: (error: Error | string) => void;
  resetScroll?: () => void;
};

function useIVProcessState({
  isKioskoScanning,
  displayError,
  onError,
  resetScroll,
  onCancelRetry,
}: UseIVProcessStateProps) {
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [failedImage, setFailedImage] = React.useState<string>();
  const [processState, setProcessState] = React.useState<STATUSES>(STATUSES.loading);
  const [isProcessModalOpen, setIsProcessModalOpen] = React.useState(false);
  const [isRetryModalOpen, setIsRetryModalOpen] = React.useState(false);
  const handlerRef = React.useRef<null | (() => void)>(null);

  const openProcessModal = useCallback(() => {
    setIsProcessModalOpen(true);
  }, []);

  const closeProcessModal = useCallback(() => {
    setIsProcessModalOpen(false);
  }, []);

  const openRetryModal = useCallback(() => {
    setIsRetryModalOpen(true);
  }, []);

  const closeRetryModal = useCallback(() => {
    setIsRetryModalOpen(false);
  }, []);

  useEffect(() => {
    if (isKioskoScanning) {
      openProcessModal();
      return;
    }

    closeProcessModal();
  }, [closeProcessModal, isKioskoScanning, openProcessModal]);

  const closeAndCleanLoadingModal = useCallback(() => {
    closeProcessModal();
    setProcessState(STATUSES.idle);
  }, [closeProcessModal]);

  useEffect(() => {
    return () => {
      closeAndCleanLoadingModal();
      resetScroll?.();
    };
  }, [closeAndCleanLoadingModal, resetScroll]);

  const onBefore = useCallback(() => {
    setProcessState(STATUSES.loading);
    openProcessModal();
  }, [openProcessModal]);

  const handleError = useCallback(
    (error: Error | FieldsErrorResponse) => {
      Analytics.track(ANALYTIC_EVENTS.ivError, {
        'Error message': getErrorMessage(error),
      });
      closeAndCleanLoadingModal();
      displayError(error);
      if (isObject(error) && 'errors' in error) {
        onError?.(error.errors?.[0]?.message);
        return;
      }

      onError?.(error as Error);
    },
    [closeAndCleanLoadingModal, displayError, onError],
  );

  const setFailureState = useCallback((nextErrorMessage?: string, image?: string) => {
    setErrorMessage(nextErrorMessage);
    setFailedImage(image);
    setProcessState(STATUSES.error);
  }, []);

  const wrapRetryable = useCallback(
    <T extends (...args: Parameters<T>) => void>(target: T) => {
      return (...args: Parameters<T>) => {
        const handler = () => target(...args);
        handlerRef.current = handler;
        return handler();
      };
    },
    [],
  );

  const handleRetryAgain = useCallback(() => {
    handlerRef.current?.();
    closeRetryModal();
  }, [closeRetryModal]);

  const handleCancelRetryAgain = useCallback(() => {
    closeAndCleanLoadingModal();
    onCancelRetry();
    closeRetryModal();
    handlerRef.current = null;
  }, [closeAndCleanLoadingModal, closeRetryModal, onCancelRetry]);

  return {
    errorMessage,
    failedImage,
    processState,
    isProcessModalOpen,
    isRetryModalOpen,
    onBefore,
    openRetryModal,
    closeAndCleanLoadingModal,
    handleError,
    setFailureState,
    wrapRetryable,
    handleRetryAgain,
    handleCancelRetryAgain,
    setProcessState,
  };
}

export {useIVProcessState};
